import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import {
  TrendingUp,
  Users,
  Clock,
  Award,
  Heart,
  Cpu,
  ShoppingBag,
  GraduationCap,
  MapPin,
  Mail,
  ArrowRight,
  Check,
} from 'lucide-react';

/**
 * Unified Careers page — one canonical /careers route reached from the main
 * home-page Footer's "Careers" link. Content spans all three ZHELTA divisions
 * (Systems, Services, Academy) so hiring is never siloed as "just Managed IT".
 *
 * Division-specific headers/footers (Systems/Academy/Store) intentionally do
 * NOT link here — the route is surfaced exclusively from the main site so
 * applicants always land on the unified experience.
 */
export default function Careers() {
  return (
    <div className="min-h-screen bg-white" data-testid="careers-page">
      <SeoHead
        title="Careers at ZHELTA — Join the team shaping IT, retail tech and practical learning"
        description="Grow your career across ZHELTA Systems, Services and Academy. Flexible schedules, continuous learning, and a supportive culture."
        path="/careers"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#faf6ee] to-white pt-20 md:pt-28 pb-16 md:pb-20">
        <div className="zh-container text-center max-w-3xl mx-auto">
          <div className="inline-block text-[12px] md:text-[13px] font-bold uppercase tracking-[0.22em] text-zinc-700 mb-5">
            Join Our Team
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-gray-900 leading-[1.05] mb-6">
            Careers at ZHELTA
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-[1.65]">
            Help people and businesses get more out of technology — across managed IT, verified
            hardware, and practical education. Grow your career in a supportive, innovative
            environment that backs you all the way.
          </p>
        </div>
      </section>

      {/* Why work with us */}
      <section className="py-16 md:py-24 bg-[#f7f7f9]">
        <div className="zh-container">
          <div className="max-w-3xl mb-12 md:mb-14">
            <div className="inline-block text-[12px] md:text-[13px] font-bold uppercase tracking-[0.22em] text-zinc-700 mb-4">
              Our culture
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05] mb-4">
              Why work with us?
            </h2>
            <div className="h-1.5 w-24 rounded-full bg-[#f5a623] mb-5" />
            <p className="text-gray-600 text-lg md:text-xl leading-[1.65]">
              We're building a unified technology platform across three divisions — ZHELTA Systems,
              Services and Academy — with a shared focus on people, innovation, and excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Pillar
              Icon={TrendingUp}
              title="Growth Opportunities"
              body="Continuous learning and career-development programs that span IT, retail ops, and education."
            />
            <Pillar
              Icon={Users}
              title="Great Team"
              body="Work with talented professionals across every division in a collaborative, feedback-rich culture."
            />
            <Pillar
              Icon={Clock}
              title="Work-Life Balance"
              body="Flexible schedules, remote-first roles where the work allows, and sensible time-off policies."
            />
            <Pillar
              Icon={Award}
              title="Competitive Benefits"
              body="Comprehensive compensation, performance-based bonuses, and full benefits packages."
            />
          </div>
        </div>
      </section>

      {/* Divisions you can join */}
      <section className="py-16 md:py-24 bg-white">
        <div className="zh-container">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
            <div className="inline-block text-[12px] md:text-[13px] font-bold uppercase tracking-[0.22em] text-zinc-700 mb-4">
              Where you could work
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
              Three divisions. Many paths.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <DivisionCard
              Icon={Cpu}
              title="ZHELTA Systems"
              body="Managed IT, cloud, cybersecurity, network infrastructure, help-desk and deployment roles."
              href="/systems"
              accent="#f5a623"
            />
            <DivisionCard
              Icon={ShoppingBag}
              title="ZHELTA Services"
              body="E-commerce, retail operations, logistics, product sourcing, and customer-experience roles."
              href="/services"
              accent="#34d17c"
            />
            <DivisionCard
              Icon={GraduationCap}
              title="ZHELTA Academy"
              body="Curriculum design, instruction, mentorship, learning-experience and student-success roles."
              href="/academy"
              accent="#60a5fa"
            />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-[#f7f7f9]">
        <div className="zh-container">
          <div className="max-w-3xl mb-12 md:mb-14">
            <div className="inline-block text-[12px] md:text-[13px] font-bold uppercase tracking-[0.22em] text-zinc-700 mb-4">
              What we offer
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05] mb-4">
              Benefits built for real life
            </h2>
            <div className="h-1.5 w-24 rounded-full bg-[#f5a623] mb-5" />
            <p className="text-gray-600 text-lg md:text-xl leading-[1.65]">
              Comprehensive benefits designed to support your professional and personal growth —
              whether you're on the help desk, in a classroom, or fulfilling orders.
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Flexible work hours',
              'Remote work options',
              'Professional development budget',
              'Latest technology tools',
              'Team events & activities',
              'Paid time off',
            ].map((b) => (
              <li
                key={b}
                className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-[0_4px_16px_rgba(0,0,0,0.03)]"
              >
                <div className="w-9 h-9 rounded-lg bg-[#fff5e0] flex items-center justify-center shrink-0">
                  <Check className="w-5 h-5 text-zinc-800" strokeWidth={2.5} />
                </div>
                <span className="text-[15px] md:text-base font-semibold text-gray-900">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Interested in joining — primary path for anyone who wants to apply.
          Previously we also had a "Currently growing our team / No Open
          Positions Right Now" block here, but that phrasing conflicted with
          the fact that specific roles are already listed through the admin.
          Removed; applicants now hit a single clear path to either send a
          resume or subscribe for updates. */}

      {/* Location + apply/subscribe CTAs */}
      <section className="py-16 md:py-24 bg-white text-zinc-900">
        <div className="zh-container grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <div className="inline-block text-[12px] md:text-[13px] font-bold uppercase tracking-[0.22em] text-zinc-700 mb-4">
              Our location
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05] mb-5">
              Based in New York
            </h2>
            <p className="text-zinc-600 text-lg leading-[1.65] mb-6">
              Our team operates from the greater New York area, supporting individuals and
              businesses across all three divisions — Systems, Services and Academy — throughout
              the region and beyond.
            </p>
            <div className="space-y-3 text-[15px] text-zinc-700">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-zinc-700" />
                <span>ZHELTA LLC · New York Metropolitan Area</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-zinc-700" />
                <a href="mailto:careers@zhelta.com" className="hover:text-zinc-700">
                  careers@zhelta.com
                </a>
              </div>
            </div>
          </div>

          {/* Apply OR subscribe — two equally prominent paths so interested
              applicants never hit a dead end. Previously this card was a
              single "Get in Touch" CTA; split into two cards to give the
              "subscribe for future updates" flow its own clear action. */}
          <div className="space-y-5">
            <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-7 md:p-8">
              <div className="w-12 h-12 rounded-xl bg-[#fff5e0] border border-[#f5a623]/40 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-zinc-700" strokeWidth={1.8} />
              </div>
              <h3 className="text-xl md:text-2xl font-bold tracking-tight mb-2">
                Interested in joining us?
              </h3>
              <p className="text-zinc-600 leading-[1.65] mb-5">
                Send us your resume and tell us which division you'd love to work in — Systems,
                Services, or Academy. We'll review it and get in touch as relevant roles open up.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="mailto:careers@zhelta.com?subject=Open application — ZHELTA Careers"
                  className="inline-flex items-center justify-center gap-2 bg-[#f5a623] hover:bg-[#e4951a] text-gray-900 font-bold rounded-full px-6 py-3 transition"
                  data-testid="careers-send-resume"
                >
                  Send Your Resume
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-zinc-100 border border-zinc-300 text-zinc-900 rounded-full px-6 py-3 transition font-semibold"
                >
                  Learn More About Us
                </Link>
              </div>
            </div>

            <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-7 md:p-8">
              <div className="w-12 h-12 rounded-xl bg-[#fff5e0] border border-[#f5a623]/40 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-[#c9a227]" strokeWidth={1.8} />
              </div>
              <h3 className="text-xl md:text-2xl font-bold tracking-tight mb-2">
                Subscribe for future updates
              </h3>
              <p className="text-zinc-600 leading-[1.65] mb-5">
                Get notified the moment new roles go live across any division. No marketing spam —
                just hiring updates, straight to your inbox.
              </p>
              <a
                href="mailto:careers@zhelta.com?subject=Subscribe — ZHELTA career updates&body=Please add me to the ZHELTA careers updates list."
                className="inline-flex items-center gap-2 bg-[#f5a623] hover:brightness-105 text-[#0e0e10] font-bold rounded-full px-6 py-3 transition"
                data-testid="careers-subscribe"
              >
                Subscribe for Updates
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Removed the trailing "Stay Updated / Get Notified" block — the
          "Subscribe for future updates" card in the section above already
          owns that action, so keeping both created a redundant CTA. */}

      <Footer />
    </div>
  );
}

function Pillar({ Icon, title, body }) {
  return (
    <div className="bg-white rounded-2xl p-7 shadow-[0_8px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300">
      <div className="w-11 h-11 rounded-xl bg-[#fff5e0] flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-zinc-800" strokeWidth={2} />
      </div>
      <h3 className="text-[17px] md:text-[18px] font-bold tracking-tight text-gray-900 mb-2 leading-snug">
        {title}
      </h3>
      <p className="text-gray-600 text-[14.5px] leading-[1.6]">{body}</p>
    </div>
  );
}

function DivisionCard({ Icon, title, body, href, accent }) {
  return (
    <Link
      to={href}
      className="group block bg-white rounded-2xl p-8 border border-gray-200 hover:border-[var(--accent)] shadow-[0_8px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_45px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300"
      style={{ ['--accent']: accent }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
        style={{ background: `${accent}1a` }}
      >
        <Icon className="w-6 h-6" style={{ color: accent }} strokeWidth={1.8} />
      </div>
      <h3 className="text-xl md:text-2xl font-bold tracking-tight text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-[1.6] mb-5">{body}</p>
      <span className="inline-flex items-center gap-1.5 text-[14px] font-bold text-gray-900 group-hover:gap-2.5 transition-all">
        Explore division
        <ArrowRight className="w-4 h-4" />
      </span>
    </Link>
  );
}
